import { Util } from 'src/shared/util/util';
import { Component, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { Integrations } from 'src/providers/integrations/integrations';
import { buildCustomFieldsPatch } from 'src/shared/services/guidebook-address-patch';

@Component({
    selector: 'app-add-custom-field',
    templateUrl: './add-custom-field.html',
    standalone: false
})

export class AddCustomFieldPage implements OnInit {
  form: UntypedFormGroup;
  isRightSideContent = true;
  customFields: any[] = [];
  editingField: any = null;
  instance: any = null;
  submitted = false;
  fieldTypes = [
    { value: 'string', viewValue: 'Text' },
    { value: 'integer', viewValue: 'Number' },
    { value: 'boolean', viewValue: 'Boolean' },
  ];
  visibilityTypes = [
    { value: true, viewValue: 'Public (Visible to all pros)' },
    { value: false, viewValue: 'Private (Visible to only you)' },
  ];
  modelTypes = [
    { value: 'Address', viewValue: 'Property' },
    { value: 'GuestReservation', viewValue: 'Reservation' },
    { value: 'Booking', viewValue: 'Job' },
    { value: 'IssueReport', viewValue: 'Task' },
    // Hidden until the server can save item values.
    // { value: 'RoomObject', viewValue: 'Item' },
  ];
  loaded = false;


  boundAddCustomField = this.addCustomField.bind(this);

constructor(
    private fb: UntypedFormBuilder,
    private storage: TidyStorage,
    private rightSidePanelService: RightSidePanelService,
    private util: Util,
    private integrations: Integrations,
  ) {
    this.form = this.fb.group({
      model_type: ['', Validators.required],
      field_name: ['', Validators.required],
      field_type: ['', Validators.required],
      visibility: ['', Validators.required],
      default_value: [''],
      value: [''],
    });
  }

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    this.rightSidePanelService.setDialogPageTitle('Custom Fields');

    // Check if we're editing an existing field
    const dialogParams = await this.storage.retrieve('dialog-params');
    if (dialogParams?.customField) {
      this.editingField = dialogParams.customField;
      this.instance = dialogParams.instance;
      this.form.patchValue({
        model_type: this.editingField.model_type,
        field_name: this.editingField.field_name,
        field_type: this.editingField.field_type,
        visibility: this.editingField.is_visible_to_homekeeper,
        default_value: this.editingField.default_value,
        value: this.instance?.custom_fields?.[this.editingField.field_key],
      });
      this.rightSidePanelService.setDialogPageTitle('Edit Custom Field');
    } else {
      this.rightSidePanelService.setDialogPageTitle('Add Custom Field');
    }
    const modelType = dialogParams?.modelType;
    if (modelType) {
      this.form.patchValue({ model_type: modelType });
    }

    await this.loadCustomFields();
    this.loaded = true;
  }

  async loadCustomFields() {
    try {
      this.customFields = await this.integrations.getCustomFields();
    } catch (error) {
      console.error('Error loading custom fields:', error);
      this.customFields = [];
    }
  }

  editField(field: any) {
    this.editingField = field;
    this.form.patchValue({
      model_type: field.model_type,
      field_name: field.field_name,
      field_type: field.field_type,
      visibility: field.is_visible_to_homekeeper,
      default_value: field.default_value,
    });
  }

  async deleteCustomField(field: any) {
    const alertOptions = {
      header: 'Delete Custom Field',
      message: `Are you sure you want to delete "${field.field_name}"?`,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Delete',
          role: 'confirm',
        },
      ],
    };

    const alert = await this.util.showConfirmAlert(alertOptions);
    alert.onDidDismiss().then(async (result) => {
      if (result.role !== 'confirm') {
        return;
      }

      const loading = await this.util.showLoading();
      try {
        await this.integrations.deleteCustomField(field.id);
        this.util.showSuccess('Custom field deleted successfully');
        if (this.isRightSideContent) {
          this.rightSidePanelService.closeRightSidePanel();
        } else {
          this.navigateBack();
        }
      } catch (error) {
        console.error(error);
        const errorMessage = error?.err?.error?.message || 'Error deleting custom field';
        this.util.showError(errorMessage);
      } finally {
        await loading.dismiss();
      }
    });
  }

  cancelEdit() {
    this.editingField = null;
    this.form.reset();
  }

  async addCustomField() {
    const loading = await this.util.showLoading();
    try {
      this.submitted = true;
      const formValue = this.form.value;
      const payload = {
        field_name: formValue.field_name,
        field_type: formValue.field_type,
        is_visible_to_homekeeper: formValue.visibility,
        default_value: formValue.default_value,
        model_type: formValue.model_type,
      };
      if (this.editingField) {
        await this.integrations.updateCustomField(this.editingField.id, payload);
        const fieldKey = this.editingField.field_key;
        const customFields = buildCustomFieldsPatch([fieldKey], this.instance?.custom_fields, { [fieldKey]: formValue.value });
        if (this.instance && Object.keys(customFields).length > 0) {
          await this.integrations.updateCustomFieldValues({ custom_fields: customFields }, this.editingField.model_type, this.instance);
        }
        this.util.showSuccess('Custom field updated successfully');
        this.navigateBack();
      } else {
        await this.integrations.addCustomField(payload);
        this.util.showSuccess('Custom field added successfully');
      }
      this.rightSidePanelService.closeRightSidePanel();

      this.submitted = false;
      await this.loadCustomFields();
      this.form.reset();
      this.editingField = null;
      await this.navigateBack();
    } catch (error) {
      const errorMessage = error?.err?.error?.message || 'Error saving custom field';
      this.util.showError(errorMessage);
    } finally {
      await loading.dismiss();
    }
  }

  async navigateBack() {
    const dialogParams = await this.storage.retrieve('dialog-params');
    const navBackRoute = dialogParams?.navBackTo;

    if (navBackRoute === 'more/custom-fields') {
      if (this.isRightSideContent) {
        this.rightSidePanelService.goBackInLayers();
      } else {
        this.rightSidePanelService.navigateTo(navBackRoute);
      }
    } else if (navBackRoute) {
      this.rightSidePanelService.navigateTo(navBackRoute);
    }
  }
}
